import React, { useState } from 'react'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { DataTable } from '@/shared/ui/DataTable'
import { PageHeader } from '@/shared/ui/PageHeader'
import { CreateModal, EditModal, FormField } from '@/shared/ui'
import type { 
  TransportTypeResponse, 
  TransportTypeCreateRequest, 
  TransportTypeUpdateRequest,
  Column
} from '@/shared/api/types'

interface TransportTypeManagerProps {
  title?: string
  description?: string
  data: TransportTypeResponse[]
  loading?: boolean
  error?: Error | null
  includeInactive?: boolean
  onToggleInactive?: (value: boolean) => void
  onCreate?: (name: string, isActive?: boolean, sortOrder?: number, requiresLicense?: boolean) => void
  onUpdate?: (id: number, name?: string, isActive?: boolean, sortOrder?: number, requiresLicense?: boolean) => void
  onDelete?: (id: number) => void
  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const TransportTypeManager: React.FC<TransportTypeManagerProps> = ({
  title = "Виды транспорта",
  description = "Управление видами транспорта для курьеров",
  data,
  loading = false,
  error = null,
  includeInactive = false,
  onToggleInactive,
  onCreate,
  onUpdate,
  onDelete,
  isCreating = false,
  isUpdating = false,
  isDeleting = false,
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingItem, setEditingItem] = useState<TransportTypeResponse | null>(null)
  
  // Форма создания
  const [createForm, setCreateForm] = useState({
    name: '',
    isActive: true,
    sortOrder: 0,
    requiresLicense: false
  })
  
  // Форма редактирования
  const [editForm, setEditForm] = useState({
    name: '',
    isActive: true,
    sortOrder: 0,
    requiresLicense: false
  })

  const handleCreate = () => {
    if (createForm.name.trim() && onCreate) {
      onCreate(
        createForm.name.trim(),
        createForm.isActive,
        createForm.sortOrder,
        createForm.requiresLicense
      )
      setCreateForm({ name: '', isActive: true, sortOrder: 0, requiresLicense: false })
      setShowCreateForm(false)
    }
  }

  const handleEdit = (item: TransportTypeResponse) => {
    setEditingItem(item)
    setEditForm({
      name: item.name,
      isActive: item.is_active,
      sortOrder: item.sort_order,
      requiresLicense: item.requires_license
    })
  }

  const handleUpdate = () => {
    if (editingItem && editForm.name.trim() && onUpdate) {
      onUpdate(
        editingItem.transport_type_id,
        editForm.name.trim(),
        editForm.isActive,
        editForm.sortOrder,
        editForm.requiresLicense
      )
      setEditingItem(null)
    }
  }

  const handleDelete = (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот вид транспорта?')) {
      onDelete?.(id)
    }
  }

  const columns: Column<TransportTypeResponse>[] = [
    {
      key: 'transport_type_id',
      title: 'ID',
      width: '80px'
    },
    {
      key: 'name',
      title: 'Название'
    },
    {
      key: 'requires_license',
      title: 'Требует права',
      width: '120px',
      render: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          value 
            ? 'bg-orange-100 text-orange-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Да' : 'Нет'}
        </span>
      )
    },
    {
      key: 'is_active',
      title: 'Статус',
      width: '100px',
      render: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          value 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Активен' : 'Неактивен'}
        </span>
      )
    },
    {
      key: 'sort_order',
      title: 'Порядок',
      width: '100px'
    },
    {
      key: 'actions',
      title: 'Действия',
      width: '150px',
      render: (_, item: TransportTypeResponse) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEdit(item)}
            disabled={isUpdating}
          >
            Изменить
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleDelete(item.transport_type_id)}
            disabled={isDeleting}
            className="text-red-600 hover:text-red-700"
          >
            Удалить
          </Button>
        </div>
      )
    }
  ]

  if (error) {
    return (
      <>
        <PageHeader title={title} description={description} />
        <div className="flex-1 p-6 overflow-auto">
          <Card className="p-6">
            <div className="text-red-600">
              Ошибка загрузки: {error.message}
            </div>
          </Card>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader title={title} description={description}>
        <Button 
          onClick={() => setShowCreateForm(true)}
          disabled={isCreating}
        >
          {isCreating ? 'Создание...' : 'Создать'}
        </Button>
      </PageHeader>
      
      <div className="flex-1 p-6 overflow-auto">

      {/* Модальное окно создания */}
      <CreateModal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Создать новый вид транспорта"
        fields={[
          {
            name: 'name',
            label: 'Название',
            type: 'text',
            required: true,
            placeholder: 'Введите название',
            value: createForm.name,
            onChange: (value) => setCreateForm(prev => ({ ...prev, name: value }))
          },
          {
            name: 'sortOrder',
            label: 'Порядок сортировки',
            type: 'number',
            value: createForm.sortOrder,
            onChange: (value) => setCreateForm(prev => ({ ...prev, sortOrder: value }))
          },
          {
            name: 'isActive',
            label: 'Активен',
            type: 'checkbox',
            value: createForm.isActive,
            onChange: (value) => setCreateForm(prev => ({ ...prev, isActive: value }))
          },
          {
            name: 'requiresLicense',
            label: 'Требуется водительское удостоверение',
            type: 'checkbox',
            value: createForm.requiresLicense,
            onChange: (value) => setCreateForm(prev => ({ ...prev, requiresLicense: value }))
          }
        ]}
        onSubmit={handleCreate}
        onCancel={() => {
          setCreateForm({ name: '', isActive: true, sortOrder: 0, requiresLicense: false })
        }}
        isLoading={isCreating}
      />

      {/* Модальное окно редактирования */}
      <EditModal
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        title="Редактировать вид транспорта"
        fields={[
          {
            name: 'name',
            label: 'Название',
            type: 'text',
            required: true,
            placeholder: 'Введите название',
            value: editForm.name,
            onChange: (value) => setEditForm(prev => ({ ...prev, name: value }))
          },
          {
            name: 'sortOrder',
            label: 'Порядок сортировки',
            type: 'number',
            value: editForm.sortOrder,
            onChange: (value) => setEditForm(prev => ({ ...prev, sortOrder: value }))
          },
          {
            name: 'isActive',
            label: 'Активен',
            type: 'checkbox',
            value: editForm.isActive,
            onChange: (value) => setEditForm(prev => ({ ...prev, isActive: value }))
          },
          {
            name: 'requiresLicense',
            label: 'Требует права',
            type: 'checkbox',
            value: editForm.requiresLicense,
            onChange: (value) => setEditForm(prev => ({ ...prev, requiresLicense: value }))
          }
        ]}
        onSubmit={handleUpdate}
        onCancel={() => setEditingItem(null)}
        isLoading={isUpdating}
      />

      {/* Таблица данных */}
      <Card>
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          emptyMessage="Виды транспорта не найдены"
        />
      </Card>
      </div>
    </>
  )
}
